import { useEffect, useMemo, useState } from 'react';
import { ledgersApi } from '../lib/api';
import type { Ledger, LedgerType } from '../lib/types';
import { TypeAhead, type TypeAheadItem } from './TypeAhead';

interface AsyncLedgerTypeAheadProps {
  value?: Ledger | null;
  selectedId?: string;
  allowedTypes?: LedgerType[];
  excludeTypes?: LedgerType[];
  onSelect: (ledger: Ledger) => void;
  disabled?: boolean;
  placeholder?: string;
}

/** Server-backed ledger picker. It queries the indexed ledger endpoint as the operator types. */
export function AsyncLedgerTypeAhead({ value, selectedId, allowedTypes, excludeTypes, onSelect, disabled, placeholder = 'Search ledger…' }: AsyncLedgerTypeAheadProps) {
  const [items, setItems] = useState<Ledger[]>(value ? [value] : []);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const allowedKey = allowedTypes?.join(',') ?? '';
  const excludeKey = excludeTypes?.join(',') ?? '';

  useEffect(() => {
    let cancelled = false;
    if (selectedId && !value) {
      ledgersApi.get(selectedId).then((ledger) => { if (!cancelled) setItems((current) => [ledger, ...current.filter((item) => item.id !== ledger.id)]); }).catch(() => undefined);
    }
    const timer = window.setTimeout(() => {
      setLoading(true);
      ledgersApi.list({ q: query || undefined, limit: 200 }).then(({ items: next }) => {
        if (cancelled) return;
        const merged = [...(value ? [value] : []), ...next];
        const seen = new Set<string>();
        setItems(merged.filter((ledger) => {
          if (seen.has(ledger.id) || !ledger.isActive || (allowedTypes && !allowedTypes.includes(ledger.ledgerType)) || excludeTypes?.includes(ledger.ledgerType)) return false;
          seen.add(ledger.id); return true;
        }));
      }).finally(() => { if (!cancelled) setLoading(false); });
    }, 120);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [allowedKey, excludeKey, query, selectedId, value]);

  const typeAheadItems = useMemo<TypeAheadItem[]>(() => items.map((ledger) => ({ key: ledger.id, label: ledger.name, sublabel: loading ? 'Loading…' : ledger.ledgerType })), [items, loading]);

  return <TypeAhead items={typeAheadItems} disabled={disabled} placeholder={loading ? 'Searching ledgers…' : placeholder} onQueryChange={setQuery} onSelect={(item) => {
    const ledger = items.find((candidate) => candidate.id === item.key);
    if (ledger) onSelect(ledger);
  }} />;
}
